import React, { useEffect } from 'react';
import SEO from './SEO';
import { Link } from 'react-router-dom';

const Opus3DTerms: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections = [
        {
            title: "1. Acceptance of Terms",
            content: (
                <div className="space-y-4">
                    <p>
                        By accessing or using the Opus 3D application operated by OmniviewLabs ("we", "us", or "our"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not access or use the application.
                    </p>
                    <p>
                        We reserve the right to modify these terms at any time. Continued use of Opus 3D after any changes constitutes your acceptance of the revised terms. We will notify users of significant changes via an in-app notice or email.
                    </p>
                </div>
            ),
        },
        {
            title: "2. Account Registration",
            content: (
                <div className="space-y-4">
                    <p>
                        Access to Opus 3D requires an account. Accounts are created by OmniviewLabs and assigned to authorised users within your organisation. To use the application:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>You must be at least 18 years of age or the age of majority in your jurisdiction.</li>
                        <li>You must provide accurate and complete information when your account is set up.</li>
                        <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
                        <li>You must notify us immediately at <a href="mailto:info@omniviewlabs.com" className="text-[#FF5C00] font-bold hover:underline">info@omniviewlabs.com</a> if you suspect any unauthorised access to your account.</li>
                        <li>Each set of credentials may only be used by the individual they were issued to.</li>
                    </ul>
                </div>
            ),
        },
        {
            title: "3. User Responsibilities",
            content: (
                <div className="space-y-4">
                    <p>As a user of Opus 3D, you are responsible for:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>All activity that occurs under your account.</li>
                        <li>Ensuring that all content you upload, create, or share complies with applicable laws and these terms.</li>
                        <li>Keeping your device and login credentials secure.</li>
                        <li>Using the application only for its intended business purposes.</li>
                        <li>Reporting any bugs, security vulnerabilities, or misuse to us promptly.</li>
                    </ul>
                </div>
            ),
        },
        {
            title: "4. Acceptable Use",
            content: (
                <div className="space-y-4">
                    <p>You agree not to use Opus 3D to:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Violate any applicable local, national, or international law or regulation.</li>
                        <li>Upload, transmit, or distribute any content that is unlawful, defamatory, obscene, or infringing on third-party intellectual property rights.</li>
                        <li>Attempt to gain unauthorised access to any part of the application, its servers, or related systems.</li>
                        <li>Interfere with or disrupt the integrity or performance of the application or its data.</li>
                        <li>Reverse-engineer, decompile, or disassemble any part of the application.</li>
                        <li>Use automated tools, scrapers, or bots to access or collect data from the application without our prior written consent.</li>
                        <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
                    </ul>
                    <p>
                        We reserve the right to investigate and take appropriate action against anyone who violates these provisions, including suspending or terminating accounts.
                    </p>
                </div>
            ),
        },
        {
            title: "5. Intellectual Property",
            content: (
                <div className="space-y-4">
                    <p>
                        All content, features, and functionality of Opus 3D — including but not limited to the software, 3D models provided by OmniviewLabs, design templates, graphics, logos, and user interface elements — are the exclusive property of OmniviewLabs or its licensors and are protected by copyright, trademark, and other intellectual property laws.
                    </p>
                    <p>
                        You are granted a limited, non-exclusive, non-transferable, revocable licence to use the application solely for your organisation's internal business purposes. You may not:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Copy, reproduce, or distribute OmniviewLabs's proprietary content without written permission.</li>
                        <li>Modify or create derivative works of the application or its assets.</li>
                        <li>Use OmniviewLabs trademarks or branding in any way that implies endorsement without prior consent.</li>
                    </ul>
                    <p>
                        Content you create within the application (such as your product configurations, quotations, and business data) remains your property, subject to the licence you grant us to store and process it as described in our Privacy Policy.
                    </p>
                </div>
            ),
        },
        {
            title: "6. AI-Generated Content Disclaimer",
            content: (
                <div className="space-y-4">
                    <p>
                        Opus 3D incorporates artificial intelligence features, including AI-powered room analysis and product suggestions ("AI Quick Design"). Please note:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><span className="font-bold text-[#021226]">AI outputs are suggestions only.</span> Results generated by AI features are not guaranteed to be accurate, complete, or suitable for any specific purpose.</li>
                        <li><span className="font-bold text-[#021226]">Human review required.</span> All AI-generated product recommendations, design suggestions, and configurations should be reviewed and verified by a qualified person before acting on them commercially.</li>
                        <li><span className="font-bold text-[#021226]">No professional advice.</span> AI outputs do not constitute professional interior design, architectural, or commercial advice.</li>
                        <li><span className="font-bold text-[#021226]">Third-party AI services.</span> AI features are powered by third-party providers (including Google Generative AI). By using these features, you acknowledge that your uploaded images may be processed by those third-party services, subject to their own terms and privacy policies.</li>
                        <li><span className="font-bold text-[#021226]">Limitation of liability.</span> OmniviewLabs is not liable for any loss, damage, or decision made in reliance on AI-generated content.</li>
                    </ul>
                </div>
            ),
        },
        {
            title: "7. Subscription and Billing",
            content: (
                <div className="space-y-4">
                    <p>
                        Opus 3D is available on a free tier as well as paid monthly and annual subscription plans. The following terms apply to paid subscriptions:
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Subscription plans and pricing are communicated directly by our team. Paid subscriptions are arranged by contacting us via email or phone.</li>
                        <li>We do not collect, store, or process any payment card details, bank information, or UPI credentials through the application.</li>
                        <li>Subscription fees are non-refundable except where required by law or as expressly agreed in writing by OmniviewLabs.</li>
                        <li>Credits included in your subscription plan expire at the end of the applicable billing period unless otherwise stated.</li>
                        <li>We reserve the right to modify subscription pricing with reasonable advance notice to users.</li>
                        <li>Downgrading or cancelling your plan may restrict access to features or data associated with the higher tier.</li>
                    </ul>
                </div>
            ),
        },
        {
            title: "8. Account Suspension and Termination",
            content: (
                <div className="space-y-4">
                    <p>We may suspend or terminate your account, with or without notice, if:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>You breach any provision of these Terms of Use.</li>
                        <li>Your subscription lapses and remains unpaid after the applicable grace period.</li>
                        <li>We reasonably believe your account has been compromised or is being used fraudulently.</li>
                        <li>We are required to do so by applicable law or court order.</li>
                        <li>We discontinue the application (with reasonable advance notice).</li>
                    </ul>
                    <p>
                        You may request deletion of your account at any time by visiting our{' '}
                        <Link to="/opus3d-delete-account" className="text-[#FF5C00] font-bold hover:underline">Account Deletion page</Link>{' '}
                        or by contacting us at{' '}
                        <a href="mailto:info@omniviewlabs.com" className="text-[#FF5C00] font-bold hover:underline">info@omniviewlabs.com</a>.
                        {' '}Upon account deletion, your data will be removed in accordance with our Privacy Policy.
                    </p>
                </div>
            ),
        },
        {
            title: "9. Limitation of Liability",
            content: (
                <div className="space-y-4">
                    <p>To the fullest extent permitted by applicable law:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>OmniviewLabs provides Opus 3D on an "as is" and "as available" basis without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.</li>
                        <li>We do not warrant that the application will be uninterrupted, error-free, or free of viruses or other harmful components.</li>
                        <li>In no event shall OmniviewLabs, its directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the application.</li>
                        <li>Our total aggregate liability to you for any claim arising from these terms or your use of Opus 3D shall not exceed the total fees paid by you to OmniviewLabs in the three (3) months preceding the claim.</li>
                    </ul>
                    <p className="text-sm text-slate-500 italic">
                        Some jurisdictions do not allow the exclusion of certain warranties or the limitation of liability for consequential damages, so the above limitations may not apply to you in full.
                    </p>
                </div>
            ),
        },
        {
            title: "10. Privacy Policy Reference",
            content: (
                <div className="space-y-4">
                    <p>
                        Your use of Opus 3D is also governed by our{' '}
                        <Link to="/opus3d-privacy" className="text-[#FF5C00] font-bold hover:underline">Privacy Policy</Link>,
                        {' '}which is incorporated into these Terms of Use by reference. The Privacy Policy explains how we collect, use, store, and protect your personal data.
                    </p>
                    <p>
                        By using Opus 3D, you confirm that you have read and understood our Privacy Policy and consent to the data practices described therein.
                    </p>
                </div>
            ),
        },
        {
            title: "11. Contact Information",
            content: (
                <div className="p-6 rounded-2xl bg-slate-50 border border-slate-100">
                    <p className="font-bold text-[#021226] mb-3">OmniviewLabs</p>
                    <p className="flex items-center gap-2">
                        <span className="text-slate-400 font-medium">Email:</span>
                        <a href="mailto:info@omniviewlabs.com" className="text-[#FF5C00] font-bold hover:underline">info@omniviewlabs.com</a>
                    </p>
                </div>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Opus 3D Terms of Use | OmniView Labs"
                description="Terms of Use for the Opus 3D mobile and web application by OmniviewLabs."
                canonical="/opus3d-terms"
            />

            {/* Header */}
            <div className="pt-32 pb-16 bg-[#F8FAFC] border-b border-slate-100 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-[0.2] pointer-events-none"></div>
                <div className="container mx-auto px-6 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF5C00]/10 text-[#FF5C00] text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" /></svg>
                        Legal
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-[#021226] mb-6 tracking-tight">Terms of Use</h1>
                    <p className="text-lg text-slate-500 font-medium mb-2">Opus 3D Mobile & Web Application</p>
                    <p className="text-sm text-slate-400 font-bold uppercase tracking-widest">Last updated: June 14, 2026</p>
                </div>
            </div>

            <div className="container mx-auto px-6 py-20">
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-16">
                        {sections.map((section, index) => (
                            <section key={index} className="scroll-mt-32">
                                <h2 className="text-2xl md:text-3xl font-black text-[#021226] mb-6 tracking-tight flex items-center gap-4">
                                    <span className="w-1.5 h-8 bg-[#FF5C00] rounded-full hidden md:block"></span>
                                    {section.title}
                                </h2>
                                <div className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed font-medium">
                                    {section.content}
                                </div>
                            </section>
                        ))}
                    </div>

                    <div className="mt-24 pt-12 border-t border-slate-100 text-center">
                        <p className="text-sm text-slate-400 mb-8 font-bold uppercase tracking-widest">
                            &copy; 2026 OmniviewLabs. All rights reserved.
                        </p>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-[#021226] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#003B5C] transition-all active:scale-95 shadow-xl"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Opus3DTerms;
